/* eslint-disable max-lines-per-function */
import React, { ChangeEvent, useEffect, useState } from 'react';
import Big from 'big.js';
import Alert from 'components/Alert';
import Beneficiaries from 'components/Molecules/Drawdowns/CreateDrawdownDrawer/Beneficiaries';
import Tooltip from 'components/Tooltip/Tooltip';
import { FormikProps } from 'formik';
import i18n from 'i18n/config';
import t from 'utils/translationHelper';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import { MassDrawdownValidationDto, TradeDto } from '@alpha/fx-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import {
  Box,
} from '@alpha/ui-lib/ui/external';
import { RadioButtonGroup } from '@alpha/ui-lib/ui/RadioButton';
import { StyledKeyValueTable, TKeyValue } from '@alpha/ui-lib/ui/table';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleRight, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useAuthorization from '../../../../../hooks/useAuthorization';
import { UserRole } from '../../../../../models/user';
import { formatNumber } from '../../../../../utils/currency.helpers';
import {
  FormikType,
  MassDrawdownRequestForm,
} from '../formData';
import { Section } from '../NewMassDrawdownDrawer';

import useStyles from './index.styles';

interface SettlementProps {
  handleSetSection: (_section: Section) => void,
  form?: FormikProps<MassDrawdownRequestForm>,
  buyCurrencyAccount?: CurrencyAccountDto,
  sellCurrencyAccount?: CurrencyAccountDto,
  paymentsForm: FormikProps<FormikType>,
  validatedDrawdownsResponse?: MassDrawdownValidationDto,
  selectedTrades: TradeDto[],
  setOpenPaymentClearWarning?: React.Dispatch<React.SetStateAction<boolean>>,
  setWarningModalConfirmationAction: (type: 'CLEAR' | 'CLOSE' | 'REMOVE' | 'BACK_TO_DRAWDOWN') => void,
}

const Settlement: React.FC<SettlementProps> = ({
  handleSetSection, form, buyCurrencyAccount, sellCurrencyAccount,
  paymentsForm, validatedDrawdownsResponse,
  selectedTrades,
  setOpenPaymentClearWarning,
  setWarningModalConfirmationAction,
}) => {
  const classes = useStyles();
  const [remitPayment, setRemitPayment] = useState<boolean>(true);
  const [remitAmount, setRemitAmount] = useState<number>(0);
  const [drawdownData, setDrawdownData] = useState<TKeyValue[]>([]);

  const { authorized: isPaymentsInputter } = useAuthorization([[UserRole.PAYMENTS_INPUTTER]]);

  const styledText = (
    <span>
      <span style={{ fontWeight: 600 }}>{t('please_note^')}</span>
      {' '}
      <span style={{ fontWeight: 400 }}>
        {t('any_payments_created_as_part_of_the_drawdown_request_will_be_processed_once_we_receive_settlement')}
      </span>
    </span>
  );

  const paymentsTotalAmount = (): number => {
    if (paymentsForm?.values?.payments) {
      const payments = paymentsForm?.values?.payments;
      let total = 0;
      for (let i = 0; i < payments.length; i += 1) {
        total += +(payments[i].amount || 0);
      }
      return total;
    }
    return 0;
  };

  const calculateDrawdownAmount = (sellCurrency: string, buyCurrency: string, drawdowns: any): string => {
    let totalSellAmount = 0;
    let totalBuyAmount = 0;

    drawdowns?.forEach((formValue) => {
      const sellAmount = formValue.sellAmount || 0;
      const buyAmount = formValue.buyAmount || 0;

      totalSellAmount += sellAmount;
      totalBuyAmount += buyAmount;
    });

    const buyAmountFormatted = formatNumber(+totalBuyAmount || 0, 2);
    const sellAmountFormatted = formatNumber(+totalSellAmount || 0, 2);
    return `${sellAmountFormatted} ${sellCurrency} | ${buyAmountFormatted} ${buyCurrency}`;
  };

  const getContractNumber = (id: string): string => {
    const trade = selectedTrades?.find((tr) => tr.id === id);
    return trade?.contractNumber || '';
  };

  useEffect(() => {
    const newDrawdownData: TKeyValue[] = [
      {
        key: t('number_of_forward_contracts'),
        value:
 (<Tooltip
   title={validatedDrawdownsResponse?.validatedDrawdowns?.map((drawdown) => (
     <div>
       <span style={{ fontWeight: '400', fontSize: '12px', color: '#1E8777' }}>
         {' '}
         {getContractNumber(drawdown?.tradeId || '')}
         {' '}
       </span>
       -
       <span style={{ marginLeft: '4px' }}>
         {`${formatNumber(+drawdown.sellAmount, 2)} ${form?.values.sellCurrencyCode}  |  ${formatNumber(+drawdown.buyAmount, 2)} ${form?.values.buyCurrencyCode}`}
       </span>
     </div>
   ))}
   placement="left-start"
 >
   <div>
     {selectedTrades?.length ?? '0'}
     <span className={classes.infoIcon}>
       <FontAwesomeIcon size="xs" icon={faInfoCircle as IconProp} />
     </span>
   </div>
  </Tooltip>),

      },
      { key: t('currency_pair'), value: form?.values?.currencyPair ?? '-' },
      {
        key: t('drawdown_amount'),
        value: calculateDrawdownAmount(
          form?.values?.sellCurrencyCode, form?.values?.buyCurrencyCode, validatedDrawdownsResponse?.validatedDrawdowns || [],
        ),
      },
      {
        key: t('weighted_rate'),
        value: formatNumber(form?.values?.weightedRate || 0, 4),
        boldValue: false,
      },
    ];

    if (form && remitPayment && validatedDrawdownsResponse) {
      const paymentAmountInBuyCurrency = paymentsTotalAmount();
      // Accumulate buyAmount for all drawdowns
      const totalBuyAmount = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
        (total, drawdown) => total.plus(drawdown.buyAmount || 0), new Big(0),
      ) || new Big(0);
      const remainingBalanceInBuyCurrency = totalBuyAmount.minus(paymentAmountInBuyCurrency).toNumber();

      newDrawdownData.push({
        key: t('total_payments_amount'),
        value: `${formatNumber(paymentsTotalAmount(), 2)} ${form?.values.buyCurrencyCode}`,
        boldValue: true,
      },
      {
        key: t('drawdown_amount_balance_to_assign'),
        value: `${formatNumber(+remainingBalanceInBuyCurrency, 2)} ${form?.values?.buyCurrencyCode}`,
        boldValue: true,
      });
    }
    setDrawdownData(newDrawdownData);
  }, [remitPayment, paymentsForm?.values?.payments, validatedDrawdownsResponse]);

  useEffect(() => {
    // Sum finalRemittedAmount for all drawdowns
    const totalRemittedAmount = validatedDrawdownsResponse?.validatedDrawdowns?.reduce(
      (total, drawdown) => total.plus(drawdown.finalRemittedAmount || 0), new Big(0),
    );
    setRemitAmount(totalRemittedAmount || 0); // check
  }, [validatedDrawdownsResponse]);

  useEffect(() => {
    if (paymentsForm.values.payments?.length! > 0) {
      setRemitPayment(true);
    }
  }, [
    paymentsForm.values.payments,
  ]);

  return (
    <>
      <div className={classes.container}>
        <div style={{
          display: 'flex', flexDirection: 'column', position: 'relative', padding: '30x',
        }}
        >
          <div className={classes.detailTable}>
            <StyledKeyValueTable
              testId="drawdown-data-table"
              data={drawdownData}
            />
          </div>
        </div>
        {isPaymentsInputter && (
        <div style={{
          display: 'flex', flexDirection: 'column', position: 'relative', paddingTop: '30px',
        }}
        >
          <div className={classes.remitContainer}>
            <Typography className={classes.subHeading}>
              {t('would_you_like_to_send_payments_from_this_drawdown')}
            </Typography>
            <div className={classes.radioContainer}>
              <RadioButtonGroup
                testId="drawdown-remit-payment"
                value={remitPayment}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  if (e.target.value === 'false'
                      && paymentsForm?.values.payments && (paymentsForm?.values?.payments?.length > 0)) {
                    if (setOpenPaymentClearWarning) {
                      setWarningModalConfirmationAction('BACK_TO_DRAWDOWN');
                      setOpenPaymentClearWarning(true);
                    }
                  } else {
                    setRemitPayment(e.target.value === 'true');
                  }
                }}
                items={[{
                  label: (
                    <p>
                      <strong>{t('yes')}</strong>
                      {' '}
                      |
                      {' '}
                      <span style={{ color: '#00000080' }}>{t('i_want_to_create_payments_to_have_funds_sent_onwards_by_alpha_fx')}</span>
                    </p>
                  ),
                  value: true,
                  testId: 'remit-yes',
                },
                {
                  label: (
                    <p>
                      <strong>{t('no')}</strong>
                      {' '}
                      |
                      {' '}
                      <span style={{ color: '#00000080' }}>
                        {i18n.language === 'es' ? t('curreny_will_be_added_to_currency_account')
                          : `${t('the')} ${form?.values?.buyCurrencyCode} ${t('bought_will_be_added_to_your_currency_account')} 
                      (${buyCurrencyAccount?.friendlyName || buyCurrencyAccount?.accountName}) 
                       ${t('held_by_alpha_fx')}`}
                      </span>
                    </p>
                  ),
                  value: false,
                  testId: 'remit-no',
                },
                ]}
              />

              {remitPayment && (
              <Alert
                variant="info"
                text={styledText}
              />
              )}
              {!remitPayment && (
                <Alert
                  variant="info"
                  text={`${form?.values?.buyCurrencyCode} ${formatNumber(remitAmount, 2)} ${t('will_be_available_in_your_currency_account')} (${buyCurrencyAccount?.friendlyName || buyCurrencyAccount?.accountName}) ${t('held_with_alpha_fx')}`}
                />
              )}
            </div>
          </div>
        </div>
        )}

        {isPaymentsInputter && remitPayment
          ? (
            <>
              <div style={{
                display: 'flex', flexDirection: 'column', position: 'relative', paddingTop: '10px',
              }}
              >
                <Beneficiaries
                  {...paymentsForm}
                  drawdownForm={form?.values}
                  massDrawdownId={validatedDrawdownsResponse?.massDrawdownId}
                  validatedDrawdowns={validatedDrawdownsResponse}
                  buyCurrencyAccount={buyCurrencyAccount}
                  sellCurrencyAccount={sellCurrencyAccount}
                  handleSetSection={handleSetSection}
                  setOpenPaymentClearWarning={setOpenPaymentClearWarning}
                  setWarningModalConfirmationAction={setWarningModalConfirmationAction}
                />
              </div>
            </>
          ) : (
            <Box className={classes.buttonsContainer}>
              <ActionButton
                style={{
                  background: '#F7F7F7', color: '#212529', width: '134px', whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  handleSetSection('Drawdown');
                }}
              >
                {' '}
                {t('back_to_drawdown')}
              </ActionButton>
              <ActionButton fullWidth onClick={() => handleSetSection('Submit')}>
                {t('continue_to_confirmation')}
                {' '}
                <FontAwesomeIcon icon={faAngleRight as IconProp} />
              </ActionButton>
            </Box>
          )}
      </div>
    </>
  );
};

export default Settlement;
